import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    const selectedVariantMediaId = event.detail.resource?.featured_media?.id?.toString();

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    if (selectedVariantMediaId) {
      this.#revealSelectedVariantMedia(newMediaGallery, selectedVariantMediaId);
    }
  };

  /**
   * @param {Element} mediaGallery
   * @param {string} mediaId
   */
  #revealSelectedVariantMedia(mediaGallery, mediaId) {
    customElements.whenDefined('media-gallery').then(() => {
      requestAnimationFrame(() => {
        if (typeof mediaGallery.revealMediaById === 'function') {
          mediaGallery.revealMediaById(mediaId, { onlyIfNeeded: true });
        }
      });
    });
  }

  /**
   * Reveals a product media item when variant selection moves the selected media out of view.
   * @param {string | number} mediaId - The Shopify media id to reveal.
   * @param {{ onlyIfNeeded?: boolean, behavior?: ScrollBehavior }} [options]
   * @returns {boolean}
   */
  revealMediaById(mediaId, { onlyIfNeeded = true, behavior = prefersReducedMotion() ? 'instant' : 'smooth' } = {}) {
    if (!mediaId || !this.#shouldRevealVariantMedia()) return false;

    const mediaIdSelector = `[data-media-id="${CSS.escape(mediaId.toString())}"]`;
    const media = this.#mediaElementForReveal(mediaIdSelector);
    const target = media?.closest('.product-media-container') ?? media;

    if (!(target instanceof HTMLElement)) return false;

    const selectedSlide = media?.closest('slideshow-slide');
    const didSelectSlide = selectedSlide instanceof HTMLElement ? this.#selectSlideshowMedia(selectedSlide, behavior) : false;

    if (onlyIfNeeded && this.#isMediaMeaningfullyVisible(target)) return didSelectSlide;

    target.scrollIntoView({ behavior, block: 'center', inline: 'nearest' });
    return true;
  }

  #shouldRevealVariantMedia() {
    if (this.presentation === 'carousel') return true;
    if (this.dataset.galleryIntent === 'bottom_thumbnail_carousel') return true;
    if (this.dataset.galleryIntent !== 'stacked_media_gallery') return false;
    if (this.presentation !== 'grid') return false;

    return true;
  }

  /**
   * @param {string} mediaIdSelector
   */
  #mediaElementForReveal(mediaIdSelector) {
    const gridMedia = this.querySelector(`[data-testid="media-gallery-grid"] ${mediaIdSelector}`);
    if (this.#shouldPreferGridMedia() && gridMedia) return gridMedia;

    const mediaElements = Array.from(this.querySelectorAll(mediaIdSelector));
    const visibleMedia = mediaElements.find((media) => {
      if (!(media instanceof HTMLElement)) return false;
      const target = media.closest('.product-media-container') ?? media;
      return target instanceof HTMLElement && this.#isRendered(target);
    });

    return visibleMedia || gridMedia || mediaElements[0];
  }

  #shouldPreferGridMedia() {
    return (
      this.dataset.galleryIntent === 'stacked_media_gallery' &&
      this.presentation === 'grid' &&
      matchMedia('(min-width: 750px)').matches
    );
  }

  /**
   * @param {HTMLElement} slide
   * @param {ScrollBehavior} behavior
   */
  #selectSlideshowMedia(slide, behavior) {
    if (!this.slideshow?.slides?.length) return false;

    const slideIndex = this.slideshow.slides.indexOf(slide);
    if (slideIndex < 0) return false;

    this.slideshow.select(slideIndex, undefined, { animate: behavior !== 'instant' });
    return true;
  }

  /**
   * @param {HTMLElement} element
   */
  #isRendered(element) {
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  /**
   * @param {HTMLElement} element
   */
  #isMediaMeaningfullyVisible(element) {
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);

    if (visibleWidth <= 0 || visibleHeight <= 0) return false;

    const minimumVisibleWidth = Math.min(rect.width * 0.5, 240);
    const minimumVisibleHeight = Math.min(rect.height * 0.2, 140);

    return visibleWidth >= minimumVisibleWidth && visibleHeight >= minimumVisibleHeight;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
